import { useCanGoBack, useRouter } from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Escape leaves a full-page view: back to wherever the user came from inside
 * the app, or — opened directly (new tab, pasted link) — to `fallback`, so the
 * key never walks the browser out of the app.
 *
 * Navigation goes through the router, so a page using
 * `useUnsavedRouteGuard` still gets its discard prompt; Escape is just another
 * way of asking to leave.
 *
 * Escape is a key half the page already uses, so it only counts when nothing
 * else wanted it — see {@link shouldEscapeGoBack}.
 */
export function useEscapeToGoBack({
  fallback,
  enabled = true,
}: {
  /** Where to go when there is no in-app page to return to. */
  fallback: () => void;
  /** Off while the page must not be left by a stray key, e.g. mid-save. */
  enabled?: boolean;
}): void {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  // Read live from the listener, so it is registered once rather than torn
  // down and re-added on every render that passes a new `fallback` closure.
  // Updated in a layout effect, not during render: a render React discards
  // (concurrent mode) must not leave the listener reading uncommitted props —
  // e.g. `enabled: true` from an abandoned render while a save is pending.
  const latest = useRef({ fallback, enabled, canGoBack });
  useLayoutEffect(() => {
    latest.current = { fallback, enabled, canGoBack };
  });

  useEffect(() => {
    // On `window`, in the BUBBLE phase: Radix's layers (Select, Popover,
    // DatePicker, Dialog, the discard prompt) listen on the document in the
    // capture phase and mark the event handled when Escape closes them, so by
    // the time it reaches here `defaultPrevented` says the key was spent.
    const onKeyDown = (event: KeyboardEvent) => {
      if (!latest.current.enabled || !shouldEscapeGoBack(event)) return;
      event.preventDefault();
      if (latest.current.canGoBack) router.history.back();
      else latest.current.fallback();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [router]);
}

/**
 * OPEN overlay content — a backstop for any layer that does not mark Escape
 * handled. Qualified by Radix's `data-state="open"` rather than matching the
 * role alone: an always-mounted list (an inline Picker or Combobox renders
 * `role="listbox"`) or a persistent panel would otherwise match forever and
 * silently switch the hook off for that page.
 */
const OPEN_LAYER = ['dialog', 'alertdialog', 'menu', 'listbox']
  .map((role) => `[role="${role}"][data-state="open"]`)
  .join(', ');

/** Inputs whose Escape belongs to the field (clearing, cancelling an edit). */
const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'radio', 'submit', 'reset', 'range']);

/**
 * Whether this keydown should leave the page. Exported for the tests.
 *
 * No when: another key or a modifier chord; a held key's auto-repeat; IME
 * composition (Escape cancels the composition); something already handled it;
 * focus is in a field you type into; or an overlay is open.
 */
export function shouldEscapeGoBack(event: KeyboardEvent): boolean {
  if (event.key !== 'Escape') return false;
  if (event.defaultPrevented || event.repeat || event.isComposing) return false;
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return false;
  if (isTextEntry(event.target)) return false;
  if (document.querySelector(OPEN_LAYER)) return false;
  return true;
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  // `contenteditable` by attribute rather than `isContentEditable`: the
  // attribute is what an editor sets, and it reads the same everywhere.
  const field = target.closest(
    'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
  );
  if (!field) return false;
  if (field instanceof HTMLInputElement) return !NON_TEXT_INPUTS.has(field.type);
  return true;
}
